/**
 * @file     RentVsBuyToolView.tsx
 * @purpose  Composition root for the Pro rent vs buy tool. Hero verdict spans full width, then the workspace places the break-even chart in the wider main column with the buy/rent input cards stacked in the sticky right rail. Sensitivity sliders sit full-width below.
 */
'use client';

import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { ToolPageShell } from '../ToolPageShell';
import { useRentVsBuyState } from './useRentVsBuyState';
import { VerdictCard } from './VerdictCard';
import { BreakEvenChartCard } from './BreakEvenChartCard';
import { BuyInputsCard } from './BuyInputsCard';
import { RentInputsCard } from './RentInputsCard';
import { SensitivityCard } from './SensitivityCard';
import { CrossToolSyncBadge } from '@/views/dashboard/tools/_shared/CrossToolSyncBadge';
import { TwoColumnWorkspace } from '@/views/dashboard/tools/_shared/TwoColumnWorkspace';
import { ToolDisclaimer } from '@/components/marketing/ToolDisclaimer';

export default function RentVsBuyToolView() {
  const { inputs, result, patch } = useRentVsBuyState();

  // Brief skeleton on first mount while the profile bridge seeds defaults.
  const [hydrating, setHydrating] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setHydrating(false), 120);
    return () => clearTimeout(t);
  }, []);

  return (
    <ToolPageShell slug="rent-vs-buy">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, sm: 2.5 }, minWidth: 0 }}>
        <CrossToolSyncBadge />

        {/* HERO — full content width before the column split. */}
        <VerdictCard inputs={inputs} result={result} loading={hydrating} />

        {/*
          WORKSPACE — TwoColumnWorkspace renders rail on the left and main on the right.
          We pass the inputs as the rail (sticky) and the chart as main (fluid).
          Rationale: keeping the inputs sticky lets the user drag a slider and watch
          the chart re-render without scrolling; the chart is the fluid column so it
          can stretch as wide as the content area allows. With railWidth=420 the chart
          gets the wider share of the workspace at desktop sizes.
        */}
        <TwoColumnWorkspace
          railWidth={420}
          rail={
            <Stack spacing={2}>
              <BuyInputsCard inputs={inputs} patch={patch} dense />
              <RentInputsCard inputs={inputs} patch={patch} dense />
            </Stack>
          }
          main={
            <Stack spacing={{ xs: 2, sm: 2.5 }}>
              <BreakEvenChartCard result={result} loading={hydrating} />
              {/* Sensitivity sliders sit directly under the chart in the
                  main column so the user can drag an assumption and watch
                  the chart respond without scrolling away from it. */}
              <SensitivityCard inputs={inputs} patch={patch} />
            </Stack>
          }
        />

        <ToolDisclaimer tool="rent-vs-buy" variant="inline" />
      </Box>
    </ToolPageShell>
  );
}
